import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Package, Plus, Loader2, Trash2, Search, ChevronDown, ChevronUp, Minus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
    dem_1:        { label: 'Dém. principal', color: 'bg-white text-black border-slate-300', barBg: 'bg-white border-slate-300', textColor: 'text-black', rowBg: '' },
    encombrant:   { label: 'Encombrant',   color: 'bg-yellow-100 text-yellow-800 border-yellow-300', barBg: 'bg-yellow-100 border-yellow-300', textColor: 'text-yellow-700', rowBg: 'bg-yellow-100/60' },
    decheterie:   { label: 'Déchèterie',   color: 'bg-red-50 text-red-600 border-red-200',   barBg: 'bg-red-50 border-red-200',  textColor: 'text-red-600', rowBg: 'bg-red-50/40' },
    dem_2:        { label: 'Dém 2',        color: 'bg-sky-50 text-sky-700 border-sky-200', barBg: 'bg-sky-50 border-sky-200', textColor: 'text-sky-700', rowBg: 'bg-sky-50/40' },
    garde_meuble: { label: 'Garde meuble', color: 'bg-slate-50 text-slate-600 border-slate-200',    barBg: 'bg-slate-50 border-slate-200',   textColor: 'text-slate-600',  rowBg: 'bg-slate-100/50' },
    reste:        { label: 'Reste',        color: 'bg-gray-50 text-gray-600 border-gray-200',       barBg: 'bg-gray-50 border-gray-200',     textColor: 'text-gray-600',   rowBg: 'bg-gray-50/40' },
};
const TYPE_ORDER = ['dem_1', 'encombrant', 'decheterie', 'dem_2', 'garde_meuble', 'reste'];

const CARTON_NAMES = ['carton standard', 'carton livre', 'carton fragile', 'carton penderie', 'carton bouteille', 'carton verres'];
function sortArticles(articles) {
    return [...articles].sort((a, b) => {
        const typeA = TYPE_ORDER.indexOf(a.type_article);
        const typeB = TYPE_ORDER.indexOf(b.type_article);
        if (typeA !== typeB) return typeA - typeB;
        const isCartonA = CARTON_NAMES.includes(a.nom.toLowerCase()) ? 0 : 1;
        const isCartonB = CARTON_NAMES.includes(b.nom.toLowerCase()) ? 0 : 1;
        return isCartonA - isCartonB;
    });
}

export function VolumeCalculator({ inventaireId }) {
    const [articles, setArticles] = useState([]);
    const [volumeTotal, setVolumeTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Add form state
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [addVolume, setAddVolume] = useState('');
    const [addQte, setAddQte] = useState(1);
    const [addType, setAddType] = useState('dem_1');
    const [addDemontage, setAddDemontage] = useState(false);
    const [addLourd, setAddLourd] = useState(false);
    const [addHistory, setAddHistory] = useState([]);
    const searchRef = useRef(null);

    const recalcVolume = useCallback((arts) => {
        return Math.round(arts.filter(a => a.type_article !== 'reste').reduce((s, a) => s + a.volume * a.quantite, 0) * 100) / 100;
    }, []);

    const loadArticles = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/inventaires/${inventaireId}/articles`, { withCredentials: true });
            const sorted = sortArticles(res.data.articles || []);
            setArticles(sorted);
            setVolumeTotal(res.data.volume_total || 0);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [inventaireId]);

    useEffect(() => { loadArticles(); }, [loadArticles]);

    const saveArticlesRef = useRef(null);
    saveArticlesRef.current = async (newArticles) => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/api/inventaires/${inventaireId}/articles`, newArticles, { withCredentials: true });
            setVolumeTotal(res.data.volume_total);
        } catch (err) { toast.error('Erreur de sauvegarde'); }
        finally { setSaving(false); }
    };

    const saveArticles = (newArticles) => saveArticlesRef.current(newArticles);

    // Search base articles
    const handleSearch = async (val) => {
        setSearchText(val);
        if (val.length >= 1) {
            try {
                const res = await axios.get(`${API}/api/articles/base?search=${encodeURIComponent(val)}`, { withCredentials: true });
                setSearchResults(res.data);
                setShowDropdown(true);
            } catch (err) { console.error(err); }
        } else {
            setShowDropdown(false);
        }
    };

    const selectArticle = (base) => {
        setSearchText(base.nom);
        setAddVolume(base.volume == null || base.volume <= 0 ? '' : String(base.volume));
        setShowDropdown(false);
    };

    const handleAdd = () => {
        if (!searchText.trim()) { toast.error('Saisissez un article'); return; }
        const vol = parseFloat(addVolume) || 0;
        const nom = searchText.trim();
        // Bloque si le volume est vide / 0 (sauf type "reste" qui peut être 0)
        if (addType !== 'reste' && vol <= 0) {
            toast.error(`Volume manquant pour « ${nom} » — veuillez d'abord renseigner le volume dans Admin → Articles`, { duration: 6000 });
            return;
        }
        setArticles(prev => {
            // Cle de deduplication : nom + type + flags + VOLUME.
            // Deux articles de meme nom mais de volumes differents doivent rester
            // separes (ex: "Canape 2m3" et "Canape 3m3"), sinon on perd le 2nd volume.
            const existing = prev.find(a =>
                a.nom.toLowerCase() === nom.toLowerCase()
                && a.type_article === addType
                && a.demontage_remontage === addDemontage
                && a.objet_lourd === addLourd
                && Math.abs((a.volume || 0) - vol) < 0.001
            );
            let newList;
            if (existing) {
                newList = prev.map(a => a.id === existing.id ? { ...a, quantite: a.quantite + addQte } : a);
            } else {
                newList = [...prev, { id: crypto.randomUUID(), nom, type_article: addType, volume: vol, quantite: addQte, demontage_remontage: addDemontage, objet_lourd: addLourd }];
            }
            newList = sortArticles(newList);
            setVolumeTotal(recalcVolume(newList));
            saveArticles(newList);
            return newList;
        });
        setAddHistory(prev => [{ nom, qte: addQte, vol, date: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
        // Reset
        setSearchText(''); setAddVolume(''); setAddQte(1); setAddType('dem_1'); setAddDemontage(false); setAddLourd(false);
    };

    const deleteArticle = (id) => {
        setArticles(prev => {
            const newList = prev.filter(a => a.id !== id);
            setVolumeTotal(recalcVolume(newList));
            saveArticles(newList);
            return newList;
        });
    };

    // Volume summaries by type (exclude "reste")
    const volumeByType = {};
    TYPE_ORDER.forEach(t => { volumeByType[t] = 0; });
    articles.forEach(a => {
        if (a.type_article !== 'reste') {
            volumeByType[a.type_article] = (volumeByType[a.type_article] || 0) + a.volume * a.quantite;
        }
    });

    // Filtered lists
    const mainArticles = articles.filter(a => a.type_article !== 'reste');
    const resteArticles = articles.filter(a => a.type_article === 'reste');
    const demontageArticles = articles.filter(a => a.demontage_remontage);
    const lourdArticles = articles.filter(a => a.objet_lourd);

    if (loading) {
        return <Card className="border-slate-200"><CardContent className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#2e7cd6]" size={24} /></CardContent></Card>;
    }

    return (
        <Card className="border-slate-200" data-testid="volume-calculator">
            <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Package size={16} className="text-[#2e7cd6]" /> Calculateur de volume
                    </CardTitle>
                </div>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
                {/* Volume total avec ligne verte/rouge */}
                <div className="flex gap-4">
                    <div className={`w-1 rounded-full self-stretch shrink-0 ${mainArticles.length > 0 ? 'bg-emerald-500' : 'bg-red-400'}`} />
                    <div className="flex items-center justify-between flex-1 bg-blue-50 border border-blue-600 rounded-lg px-4 py-3">
                        <span className="text-base font-bold text-slate-700">Volume total</span>
                        <span className="text-base font-bold text-blue-600" data-testid="volume-total-badge">{volumeTotal} m³</span>
                    </div>
                </div>

                {/* Zone recherche / ajout manuel */}
                <div className="bg-slate-100 border border-slate-300 rounded-lg p-4 space-y-3">
                    {/* Sur tablette/mobile : 2 lignes (article+volume puis qté+ajouter)
                        Sur desktop large (lg+) : 1 seule ligne via lg:contents */}
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-2">
                        {/* Ligne 1 (mobile/tablette) : Article + Volume */}
                        <div className="flex items-end gap-2 lg:contents">
                            {/* Search */}
                            <div className="flex-1 relative" ref={searchRef}>
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        value={searchText}
                                        onChange={e => handleSearch(e.target.value)}
                                        onFocus={() => searchText.length >= 1 && setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        placeholder="Rechercher un article..."
                                        className="pl-9 bg-white h-12 text-base"
                                        data-testid="article-search-input"
                                    />
                                </div>
                                {showDropdown && searchResults.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                                        {searchResults.map((a, i) => (
                                            <button key={i} type="button" onMouseDown={() => selectArticle(a)} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 text-sm flex justify-between">
                                                <span className="text-slate-700">{a.nom}</span>
                                                <span className="text-slate-400 text-xs">{a.volume} m³{a.categorie ? ` · ${a.categorie}` : ''}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Volume */}
                            <div className="w-[140px] lg:w-[130px] shrink-0">
                                <label className="text-xs text-slate-500 mb-1 block text-center">Volume (m³)</label>
                                <div className="flex items-stretch border border-slate-200 rounded-md bg-white h-12 overflow-hidden focus-within:ring-2 focus-within:ring-[#2e7cd6]/40">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={addVolume}
                                        onChange={e => setAddVolume(e.target.value)}
                                        className="flex-1 min-w-0 text-center text-base h-full outline-none bg-white [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                        placeholder="0.00"
                                        data-testid="article-volume-input"
                                    />
                                    <div className="flex flex-col w-9 border-l border-slate-200 shrink-0">
                                        <button type="button" onClick={() => setAddVolume((Math.round(((parseFloat(addVolume) || 0) + 0.2) * 100) / 100).toString())} className="flex-1 flex items-center justify-center hover:bg-slate-50 active:bg-slate-100 text-slate-600 border-b border-slate-200" title="+0,2 m³" data-testid="article-volume-up"><ChevronUp size={16} /></button>
                                        <button type="button" onClick={() => setAddVolume((Math.round(Math.max(0, (parseFloat(addVolume) || 0) - 0.2) * 100) / 100).toString())} className="flex-1 flex items-center justify-center hover:bg-slate-50 active:bg-slate-100 text-slate-600" title="-0,2 m³" data-testid="article-volume-down"><ChevronDown size={16} /></button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ligne 2 (mobile/tablette) : Quantité + Ajouter */}
                        <div className="flex items-end gap-2 lg:contents">
                            {/* Quantité */}
                            <div className="flex-1 lg:flex-none lg:w-[140px] shrink-0">
                                <label className="text-xs text-slate-500 mb-1 block text-center">Qté</label>
                                <div className="flex items-center border border-slate-200 rounded-md bg-white h-12">
                                    <button type="button" onClick={() => setAddQte(Math.max(1, addQte - 1))} className="px-3 h-full hover:bg-slate-50 text-slate-500 active:bg-slate-100"><Minus size={16} /></button>
                                    <input type="number" min="1" value={addQte} onChange={e => setAddQte(Math.max(1, parseInt(e.target.value) || 1))} className="flex-1 w-full text-center text-base font-semibold border-x border-slate-200 h-full outline-none" />
                                    <button type="button" onClick={() => setAddQte(addQte + 1)} className="px-3 h-full hover:bg-slate-50 text-slate-500 active:bg-slate-100"><Plus size={16} /></button>
                                </div>
                            </div>

                            {/* Ajouter */}
                            <Button type="button" onClick={handleAdd} className="gap-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shrink-0 h-12 px-6 text-base font-semibold flex-1 lg:flex-none" data-testid="add-article-btn">
                                <Plus size={18} /> Ajouter
                            </Button>
                        </div>
                    </div>

                    {/* Type radio + options : 3 lignes sur tablette/mobile, plus large pour le tactile */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                        {TYPE_ORDER.filter(t => t !== 'dem_1' && t !== 'reste').map(t => (
                            <label key={t} className="flex items-center gap-2 cursor-pointer text-base px-2 py-2 rounded-md hover:bg-white/60 active:bg-white/80 transition-colors">
                                <input type="radio" name="add-type" checked={addType === t} onChange={() => setAddType(t)} className="accent-blue-600 h-5 w-5 shrink-0" />
                                <span className={TYPE_CONFIG[t].textColor + ' font-semibold'}>{TYPE_CONFIG[t].label}</span>
                            </label>
                        ))}
                        <label className="flex items-center gap-2 cursor-pointer text-base px-2 py-2 rounded-md hover:bg-white/60 active:bg-white/80 transition-colors">
                            <input type="radio" name="add-type" checked={addType === 'reste'} onChange={() => setAddType('reste')} className="accent-blue-600 h-5 w-5 shrink-0" />
                            <span className={TYPE_CONFIG.reste.textColor + ' font-semibold'}>{TYPE_CONFIG.reste.label}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-base px-2 py-2 rounded-md hover:bg-white/60 active:bg-white/80 transition-colors col-span-2 sm:col-span-2">
                            <input type="checkbox" checked={addDemontage} onChange={e => setAddDemontage(e.target.checked)} className="h-5 w-5 rounded border-slate-300 shrink-0" />
                            <span className="text-slate-600 font-medium">Démontage / Remontage</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-base px-2 py-2 rounded-md hover:bg-white/60 active:bg-white/80 transition-colors col-span-2 sm:col-span-1">
                            <input type="checkbox" checked={addLourd} onChange={e => setAddLourd(e.target.checked)} className="h-5 w-5 rounded border-slate-300 accent-orange-500 shrink-0" />
                            <span className="text-orange-600 font-medium">Objet lourd</span>
                        </label>
                    </div>
                </div>

                {/* Détail toujours visible - Articles, Reste, Démontage, Lourds */}
                <div className="space-y-6">

                    <div className="h-4"></div>

                    {/* Articles table - grouped by type with visual separation */}
                    {mainArticles.length > 0 && (
                        <div className="border border-slate-200 rounded overflow-hidden">
                            <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="text-left px-5 py-3 font-semibold text-slate-600 border-b border-slate-200 border-r border-r-slate-200">Article</th>
                                        <th className="text-center px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 border-r border-r-slate-200 w-[100px]">Qté</th>
                                        <th className="text-center px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 border-r border-r-slate-200 w-[120px]">Volume</th>
                                        <th className="text-center px-4 py-3 font-semibold text-slate-600 border-b border-slate-200 w-[100px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const rows = [];
                                        let lastType = null;
                                        mainArticles.forEach((article) => {
                                            const tc = TYPE_CONFIG[article.type_article] || TYPE_CONFIG.dem_1;
                                            const lineVol = Math.round(article.volume * article.quantite * 100) / 100;
                                            const isNewGroup = lastType !== null && article.type_article !== lastType;
                                            const isCarton = CARTON_NAMES.includes(article.nom.toLowerCase());
                                            rows.push(
                                                <tr key={article.id} className={tc.rowBg} style={isNewGroup ? { borderTop: '2px solid #cbd5e1' } : {}} data-testid={`article-row-${article.id}`}>
                                                    <td className="px-5 py-1.5 text-slate-800 border-b border-slate-100 border-r border-r-slate-100">{article.nom}</td>
                                                    <td className={`text-center px-4 py-1.5 font-medium border-b border-slate-100 border-r border-r-slate-100 ${isCarton ? 'bg-emerald-100 text-emerald-700' : 'text-slate-700'}`}>{article.quantite}</td>
                                                    <td className="text-center px-4 py-1.5 text-slate-700 border-b border-slate-100 border-r border-r-slate-100">{lineVol.toFixed(2)} m³</td>
                                                    <td className="text-center px-4 py-1.5 border-b border-slate-100">
                                                        <button type="button" onClick={() => deleteArticle(article.id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Supprimer">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                            lastType = article.type_article;
                                        });
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {mainArticles.length === 0 && (
                        <div className="text-center py-6 text-slate-400 text-sm">Aucun article ajouté. Utilisez la recherche ci-dessus.</div>
                    )}

                    {/* Volume summary by type (exclude reste) */}
                    {mainArticles.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {TYPE_ORDER.filter(t => t !== 'reste' && (volumeByType[t] || 0) > 0).map(t => {
                                const vol = Math.round((volumeByType[t] || 0) * 100) / 100;
                                const tc = TYPE_CONFIG[t];
                                return (
                                    <div key={t} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm ${tc.barBg}`}>
                                        <span className={`font-normal ${tc.textColor}`}>{tc.label}</span>
                                        <span className={`border-l border-current/20 pl-2 ${tc.textColor} font-bold`}>{vol.toFixed(2)} m³</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Articles à démonter / remonter */}
                    {demontageArticles.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-blue-700 mb-2">Articles à démonter / remonter</h4>
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-blue-50 border-b border-blue-100">
                                            <th className="text-left px-4 py-2 font-medium text-blue-700">Article</th>
                                            <th className="text-right px-4 py-2 font-medium text-blue-700 w-[80px]">Qté</th>
                                            <th className="text-center px-4 py-2 font-medium text-blue-700 w-[50px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {demontageArticles.map((a) => (
                                            <tr key={a.id} className="border-b border-slate-100 last:border-0">
                                                <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                <td className="text-center px-2 py-2">
                                                    <button type="button" onClick={() => deleteArticle(a.id)} className="p-1 rounded hover:bg-red-50 text-orange-500 hover:text-red-600 transition-colors" title="Supprimer"><Trash2 size={14} /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Objets lourds */}
                    {lourdArticles.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-orange-600 mb-2">Objets lourds</h4>
                            <div className="border border-orange-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-orange-50 border-b border-orange-100">
                                            <th className="text-left px-4 py-2 font-medium text-orange-700">Article</th>
                                            <th className="text-right px-4 py-2 font-medium text-orange-700 w-[80px]">Qté</th>
                                            <th className="text-center px-4 py-2 font-medium text-orange-700 w-[50px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lourdArticles.map((a) => (
                                            <tr key={a.id} className="border-b border-orange-50 last:border-0 bg-yellow-50/50">
                                                <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                <td className="text-center px-2 py-2">
                                                    <button type="button" onClick={() => deleteArticle(a.id)} className="p-1 rounded hover:bg-red-50 text-orange-500 hover:text-red-600 transition-colors" title="Supprimer"><Trash2 size={14} /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Articles à ne pas prendre */}
                    {resteArticles.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-slate-600 mb-2">Articles à ne pas prendre</h4>
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="text-left px-4 py-2 font-medium text-gray-600">Article</th>
                                            <th className="text-right px-4 py-2 font-medium text-gray-600 w-[80px]">Qté</th>
                                            <th className="text-center px-4 py-2 font-medium text-gray-600 w-[50px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resteArticles.map((a) => (
                                            <tr key={a.id} className="border-b border-gray-100 last:border-0">
                                                <td className="px-4 py-2 text-slate-700">{a.nom}</td>
                                                <td className="text-right px-4 py-2 text-slate-700">{a.quantite}</td>
                                                <td className="text-center px-2 py-2">
                                                    <button type="button" onClick={() => deleteArticle(a.id)} className="p-1 rounded hover:bg-red-50 text-orange-500 hover:text-red-600 transition-colors" title="Supprimer"><Trash2 size={14} /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {saving && (
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Loader2 size={12} className="animate-spin" /> Sauvegarde...
                        </div>
                    )}
                </div>
                </CardContent>
        </Card>
    );
}
